/**
 * Edit Plans batch matrix modal
 *
 * One-screen matrix for changing tier + add-on assignments across all active
 * clinicians in a single confirm. Rows highlight when changed; the footer
 * summarizes the total cost shift. Trial orgs get a billing-interval preview
 * toggle at the top so they can see what they'll be charged after conversion.
 *
 * Reuses the same grants-driven inclusion + commit-time double-billing guard
 * as the Person Card modal (`getAddOnsIncludedInTier` over `bundle.grants[]`).
 */

import { useEffect } from 'react';
import { SegmentedControl } from '@components/primitives/SegmentedControl';
import { formatPrice } from '@features/subscriptions';
import { ProviderBadge, ScrollFade } from '@jotpsych/ui';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getRoleDisplayName, ROLES } from '@utils/roleUtils';

import type { OrganizationMemberTableRow } from '../../types';

import { useEditPlans } from './useEditPlans';

interface EditPlansModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * All active members. Non-clinicians are filtered out internally since they
   * cannot have tier / add-on assignments.
   */
  members: OrganizationMemberTableRow[];
}

const primaryRole = (member: OrganizationMemberTableRow): string => {
  const preferred = [
    ROLES.CLINICIAN,
    ROLES.SUPERVISOR,
    ROLES.PRACTICE_ADMINISTRATOR,
    ROLES.BILLING_ADMINISTRATOR,
    ROLES.OPERATIONS_SUPPORT,
  ];
  for (const role of preferred) {
    if (member.roles.includes(role)) return getRoleDisplayName(role);
  }
  return member.roles[0] ? getRoleDisplayName(member.roles[0]) : '';
};

export function EditPlansModal({ open, onClose, onSuccess, members }: EditPlansModalProps) {
  // Matrix is clinician-only — non-clinicians have no tier / add-on surface.
  const eligibleMembers = members.filter(m => m.roles.includes(ROLES.CLINICIAN));

  const card = useEditPlans({ members: eligibleMembers, open, onSuccess });

  // Reset edits + fire opened analytic when modal opens.
  useEffect(() => {
    if (open) {
      card.reset();
      card.reportOpened();
    }
    // card.reset/reportOpened are stable from useCallback in the hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (card.isSaving) return;
    onClose();
  };

  const delta = card.newTotal - card.originalTotal;
  const footerLabel = !card.hasChanges
    ? 'No changes'
    : card.isTrialing
      ? `Save plan — ${formatPrice(card.newTotal, card.totalCurrency)}/mo after trial`
      : `Confirm changes — ${formatPrice(card.newTotal, card.totalCurrency)}/mo`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Edit Plans
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {card.isTrialing
                ? "Set up your team's access for when your trial ends. You won't be charged until the trial is over."
                : 'Adjust tiers and add-ons for your team. Changes take effect when you confirm.'}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={card.isSaving}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Trial-only billing-interval preview toggle */}
      {card.isTrialing && (
        <Box
          sx={{
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: theme => alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Preview billing cycle
          </Typography>
          {/*
            Use the shared `SegmentedControl` rather than MUI's ToggleButtonGroup
            so the billing-interval toggle matches every other interval / mode
            switch across the app (RatesTab, ModeToggle, OrgScheduleToggle,
            ViewModeSelector). The Annual option carries an inline chip for the
            10% discount call-out.
          */}
          <SegmentedControl
            size="small"
            ariaLabel="Preview billing cycle"
            value={card.previewInterval}
            onChange={value =>
              card.setPreviewInterval(value as 'month' | 'year')
            }
            options={[
              { value: 'month', label: 'Monthly' },
              {
                value: 'year',
                label: (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    Annual
                    <Chip
                      label="−10%"
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  </Box>
                ),
              },
            ]}
          />
        </Box>
      )}

      <DialogContent
        dividers
        sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}
      >
        <ScrollFade>
          {card.isCatalogLoading ? (
            <Box sx={{ p: 3 }}>
              <Skeleton variant="rounded" sx={{ height: 280 }} />
            </Box>
          ) : eligibleMembers.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No clinicians to manage. Invite a clinician to use Edit Plans.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              {/*
                Edge cells get pl/pr: 3 so the first column's text aligns with
                the DialogTitle copy and the last column with DialogActions
                (24px). The table itself stays full-width so the header-row
                background and row hover/highlight reach the dialog edges.
              */}
              <Table
                size="small"
                stickyHeader
                sx={{
                  '& .MuiTableCell-root:first-of-type': { pl: 3 },
                  '& .MuiTableCell-root:last-of-type': { pr: 3 },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-head': {
                        backgroundColor: theme => alpha(theme.palette.grey[500], 0.08),
                        fontWeight: 600,
                      },
                    }}
                  >
                    <TableCell>Member</TableCell>
                    <TableCell>Tier</TableCell>
                    {card.addOns.map(addOn => (
                      <TableCell key={addOn.bundle_id} align="center" sx={{ px: 1 }}>
                        {addOn.name}
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', color: 'text.secondary', fontWeight: 400 }}
                        >
                          {formatPrice(
                            card.effectiveInterval === 'year'
                              ? Math.round(addOn.yearly_unit_amount / 12)
                              : addOn.monthly_unit_amount,
                            addOn.currency,
                          )}
                          /mo
                        </Typography>
                      </TableCell>
                    ))}
                    <TableCell align="right">Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eligibleMembers.map(member => {
                    const effective = card.getEffectiveState(member.userId);
                    const changed = card.isMemberChanged(member.userId);
                    const included = card.includedByTier(effective.tierId);
                    const effectiveCost = card.computeMemberCost(
                      effective.tierId,
                      effective.addOnIds,
                      card.effectiveInterval,
                    );
                    // Original cost reflects the org's actual interval (not the
                    // preview toggle) so trial users can see "today's commitment
                    // vs. post-trial in the previewed cycle." For active orgs
                    // org === effective, so the math collapses to the same.
                    const originalCost = (() => {
                      const original = member.bundles?.find(
                        b => b.bundle_type === 'tier' && b.status === 'active',
                      );
                      const tierId = original?.bundle_id || '';
                      const addOnIds = new Set(
                        member.bundles
                          ?.filter(b => b.bundle_type === 'add_on' && b.status === 'active')
                          .map(b => b.bundle_id) || [],
                      );
                      return card.computeMemberCost(tierId, addOnIds, card.effectiveInterval);
                    })();

                    return (
                      <TableRow
                        key={member.userId}
                        sx={{
                          backgroundColor: changed
                            ? theme => alpha(theme.palette.primary.main, 0.06)
                            : 'inherit',
                          transition: 'background-color 120ms ease',
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <ProviderBadge name={member.name} size="compact" />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {member.name || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {primaryRole(member)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={effective.tierId}
                            size="small"
                            onChange={e => card.setMemberTier(member.userId, e.target.value)}
                            displayEmpty
                            disabled={card.isSaving}
                            sx={{ minWidth: 160, height: 36 }}
                          >
                            <MenuItem value="" disabled>
                              <em>Select tier</em>
                            </MenuItem>
                            {card.tiers.map(tier => {
                              const monthlyCents =
                                card.effectiveInterval === 'year'
                                  ? Math.round(tier.yearly_unit_amount / 12)
                                  : tier.monthly_unit_amount;
                              return (
                                <MenuItem key={tier.bundle_id} value={tier.bundle_id}>
                                  {tier.name} — {formatPrice(monthlyCents, tier.currency)}/mo
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </TableCell>
                        {card.addOns.map(addOn => {
                          const isIncluded = included.has(addOn.bundle_id);
                          const isChecked =
                            isIncluded || effective.addOnIds.has(addOn.bundle_id);
                          return (
                            <TableCell key={addOn.bundle_id} align="center" sx={{ px: 1 }}>
                              <Checkbox
                                size="small"
                                checked={isChecked}
                                disabled={isIncluded || card.isSaving || !effective.tierId}
                                onChange={() => card.toggleMemberAddOn(member.userId, addOn.bundle_id)}
                              />
                              {isIncluded && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', fontSize: '0.65rem', mt: -0.5 }}
                                >
                                  Incl.
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {changed ? (
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  textDecoration: 'line-through',
                                  color: 'text.secondary',
                                }}
                              >
                                {formatPrice(originalCost.cents, originalCost.currency)}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'primary.main' }}
                              >
                                {formatPrice(effectiveCost.cents, effectiveCost.currency)}/mo
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatPrice(effectiveCost.cents, effectiveCost.currency)}/mo
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ScrollFade>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: theme =>
            card.hasChanges ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
        }}
      >
        <Box>
          {card.isTrialing ? (
            <Typography variant="body2" color="text.secondary">
              After trial:{' '}
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {formatPrice(card.newTotal, card.totalCurrency)}/mo
              </Box>
              {card.hasChanges && (
                <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                  (You won&apos;t be charged until your trial ends)
                </Typography>
              )}
            </Typography>
          ) : card.hasChanges ? (
            <Typography variant="body2" color="text.secondary">
              Current: {formatPrice(card.originalTotal, card.totalCurrency)}/mo → New:{' '}
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {formatPrice(card.newTotal, card.totalCurrency)}/mo
              </Box>
              <Box
                component="span"
                sx={{
                  ml: 1,
                  fontWeight: 600,
                  color: delta > 0 ? 'warning.main' : 'success.main',
                }}
              >
                ({delta > 0 ? '+' : ''}
                {formatPrice(delta, card.totalCurrency)}/mo)
              </Box>
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Total: {formatPrice(card.originalTotal, card.totalCurrency)}/mo
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={card.isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={card.applyChanges}
            disabled={card.isSaving || !card.hasChanges}
            startIcon={card.isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {card.isSaving ? 'Saving…' : footerLabel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
